import { basePogWalk, pairKeyOf, pairPriorities, walkOrderedByCdt0 } from "./basePogWalk";
import {
  CDT0_NAME_BUDGET,
  DEFAULT_COLUMN_ATTR,
  deriveZoneShareRules,
  deriveStrata,
  materializePartitionRules,
  materializeRestrictionRows,
  namedSectionOf,
  sectionNameOf,
  withRowHomeStrata,
  type BayPartitionResult,
  type PartitionRules,
  type ReplicaRestrictionRow,
  type SectionNamingDraft,
  type StrataResult,
  type StratumPlan,
  zonesFromShippedOrder,
} from "./bayPartition";
import { measureBlockingOf, VERTICAL_SCORE, type BlockingScope } from "./blockOrientation";
import { buildLayout } from "./buildLayout";
import { collapse4, compoundPriorities, sanitizeCdtValue } from "./collapse4";
import type { LevelRole } from "./cdtLevelPlan";
import { NONE_VALUE, inferTree, type TreeNode } from "./levelInference";
import { SIZE_LIKE_SHARE, sizeLikeShare } from "./sizeFamily";
import { deriveRestrictionPlacement } from "./restrictionPlacement";
import type {
  RuleCarrier,
  RuleCarve,
  RuleNode,
  RulePool,
  RuleSet,
  RuleSkuFacts,
  RuleTreeNode,
} from "./storeRules";
import {
  FinalizeResult,
  GeneratedLayout,
  PATH_SEPARATOR,
  SkuAssignment,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  isPlaceholderValue,
} from "./studioTypes";

export const finalizeCdts = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
  authoritativeLayout?: GeneratedLayout,
): FinalizeResult => {
  const contractLayout = buildLayout(items, draft, runs);
  const deepLayout = authoritativeLayout ?? contractLayout;
  const collapse = collapse4(items, draft, runs, deepLayout, contractLayout.walkOrder);
  const priorities = compoundPriorities(collapse.contract, contractLayout.walkOrder);
  const positionedItems = items.map((item) => ({
    ...item,
    placements: (deepLayout.placements[item.skuId]?.segments ?? []).map((segment) => ({
      run: segment.run,
      shelf: segment.shelf,
      xCm: segment.xCm,
      widthCm: segment.hFacings * item.facingWidthCm,
      hFacings: segment.hFacings,
    })),
  }));

  const priorityOf = (values: readonly string[], depth: number) =>
    priorities.get(values.slice(0, depth + 1).join(PATH_SEPARATOR)) ?? 0;

  const assignments: SkuAssignment[] = items.map((item) => {
    const values = collapse.contract.itemValues[item.skuId] ?? ["", "", "", ""];
    return {
      skuId: item.skuId,
      upc: item.upc,
      name: item.name,
      cdt0: values[0] ?? "",
      cdt1: values[1] ?? "",
      cdt2: values[2] ?? "",
      cdt3: values[3] ?? "",
      cdt1Priority: priorityOf(values, 1),
      cdt2Priority: priorityOf(values, 2),
      cdt3Priority: priorityOf(values, 3),
    };
  });

  // Do not send stale restrictions: Pogger would match no candidate and empty every addressed cell.
  const shippedVariants = new Set(
    Object.values(collapse.contract.itemValues)
      .map((values) => values[0] ?? "")
      .filter((value) => value !== ""),
  );

  // Bind to the first SKU's compound key; unmeasurable geometry stays null, never "*".
  const measured = draft.restrictions.map((restriction) => {
    const firstSku = restriction.skuIds[0];
    const values = firstSku ? collapse.contract.itemValues[firstSku] : undefined;
    return {
      ...restriction,
      collapsedKey: values ? values.join(PATH_SEPARATOR) : null,
      placement: deriveRestrictionPlacement(restriction.skuIds, positionedItems, runs),
    };
  });
  const restrictions: typeof measured = [];
  const staleRestrictions: { restriction: (typeof measured)[number]; reason: string }[] = [];
  for (const restriction of measured) {
    const named = restriction.cdtPath[restriction.cdtPath.length - 1]?.trim() ?? "";
    if (named !== "" && !shippedVariants.has(named)) {
      staleRestrictions.push({
        restriction,
        reason:
          `"${named}" is no longer a value this tree ships at CDT0, so the row would ` +
          "match no candidate and empty every cell it names",
      });
      continue;
    }
    restrictions.push(restriction);
  }

  return {
    contract: collapse.contract,
    verification: collapse.verification,
    assignments,
    restrictions,
    staleRestrictions,
  };
};

export interface ReplicaEmissionOptions {
  sectionAttr?: string;

  /** Optional for base-POG emission; shipping passes it so promoted zones keep their names. */
  draft?: SectionNamingDraft;

  /** The root order shipped by the tree; omitted when measuring a base POG alone. */
  cdt0Order?: readonly string[];

  rulings?: Readonly<Record<string, LevelRole>>;

  carveByRowAttribute?: boolean;
}

export interface ReplicaTreeItem {
  cdt0: string;
  cdt1: string;
  cdt2: string;
  cdt3: string;
  cdt1_priority: number;
  cdt2_priority: number;
  cdt3_priority: number;

  segment: string;

  walk_rank: number;

  path: readonly ReplicaPathStep[];
}

export interface ReplicaPathStep {
  attr: string;

  value: string;

  carrier: "rowHome" | "lossy" | "cdt2" | "cdt3" | "seq";

  part: string | null;
}

export interface ReplicaRowHome {
  cdt0: string;
  cdt1: string;
  attr: string;
  value: string;
  shelves: readonly number[];
  skuIds: readonly string[];

  shipped: boolean;
}

export interface ReplicaPool {
  cdt0: string;

  cdt1: string;

  host?: string;
  members: readonly {
    cdt1: string;
    skuIds: readonly string[];

    widthShare: number;
    reason: string;
  }[];
}

export interface ReplicaColumnViability {
  cdt0: string;
  cdt1: string;
  items: number;
  widthCm: number;

  widthShare: number;

  salesShare: number;
  subFloor: boolean;

  exempt: boolean;

  poolable: boolean;
}

export interface ReplicaFinding {
  severity: "error" | "warning";
  check: string;
  message: string;
}

export interface ReplicaOrderingRuleReport {
  cdt0: string;
  cdt1: string;
  cdt2: string;

  cdt2Attr: string | null;
  block_order: number;
  rule: string;
  attr: string | null;
  direction: string;
  labels: readonly string[];
  pinnedLast: readonly string[];
}

export interface ReplicaEmission {
  tree: {
    items: Readonly<Record<string, ReplicaTreeItem>>;
    cdt0Order: readonly string[];

    segments: readonly { cdt0: string; cdt1: string; segment: string; priority: number }[];
  };
  restrictions: {
    rules: {
      partition: PartitionRules;
      anchorRule: string;
    };

    rows: readonly ReplicaRestrictionRow[];
    anchors: readonly ReplicaRestrictionRow[];
    partition: BayPartitionResult;
    rowStrata: Readonly<Record<string, readonly number[]>>;
    bay1Anchors: Readonly<Record<string, readonly number[]>>;

    zoneSeams: readonly { leftRun: number; rightRun: number }[];

    rowHomeStrata: readonly StratumPlan[];
  };

  pins: Readonly<
    Record<string, { min_facings: number; max_facings: number; min_days_of_supply: number }>
  >;
  orderingRules: readonly ReplicaOrderingRuleReport[];

  rowHomes: readonly ReplicaRowHome[];

  pools: Readonly<Record<string, ReplicaPool>>;

  poolViability: readonly ReplicaColumnViability[];

  rules: RuleSet;
  advisories: readonly string[];
  findings: readonly ReplicaFinding[];
}

export const resolveAttrValue = (item: StudioItem, attr: string): string =>
  resolveAttrValueOver(item.attrs, attr);

export const resolveAttrValueOver = (
  attrs: Readonly<Record<string, unknown>>,
  attr: string,
): string => {
  const cut = attr.indexOf(" >= ");
  if (cut >= 0) {
    const base = attr.slice(0, cut);
    const threshold = parseFloat(attr.slice(cut + 4));
    const raw = String(attrs[base] ?? "");
    const magnitude = isPlaceholderValue(raw) ? null : raw.match(/[\d.]+/);
    const number = magnitude ? parseFloat(magnitude[0]) : NaN;
    if (Number.isNaN(number)) return NONE_VALUE;
    // Floating-point noise must not move an item across its measured cut.
    return number >= threshold - 1e-6 ? `>= ${attr.slice(cut + 4)}` : `< ${attr.slice(cut + 4)}`;
  }
  const raw = String(attrs[attr] ?? "");
  return isPlaceholderValue(raw) ? NONE_VALUE : raw;
};

export const composeLabel = (parts: readonly string[], fallback: string): string => {
  const live = parts.filter((value, index) => value !== NONE_VALUE && value !== parts[index - 1]);
  return sanitizeCdtValue(live.length > 0 ? live.join(" — ") : fallback);
};

export const emitReplicaRequest = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  options: ReplicaEmissionOptions = {},
): ReplicaEmission => {
  const sectionAttr = options.sectionAttr ?? "variant";
  const draft = options.draft;
  const rulings = options.rulings ?? {};
  // Row-based CDT0 carves are opt-in because they prevent movement between shelves.
  const carveByRowAttribute = options.carveByRowAttribute ?? false;
  const advisories: string[] = [];
  const findings: ReplicaFinding[] = [];
  const warn = (check: string, message: string) =>
    findings.push({ severity: "warning", check, message });
  const fail = (check: string, message: string) =>
    findings.push({ severity: "error", check, message });

  let walk = basePogWalk(items, runs);
  // The tree's section order partitions the wall when the studio ships one; the base pog's
  // layout only when it does not.
  const shippedZones = options.cdt0Order?.length
    ? zonesFromShippedOrder(items, sectionAttr, options.cdt0Order, {
        draft,
        maxZones: runs[0]?.bays ?? 0,
      })
    : null;
  const partitionRules: PartitionRules =
    shippedZones ?? deriveZoneShareRules(items, sectionAttr, runs.length, runs[0]);
  const partition: BayPartitionResult = materializePartitionRules(partitionRules, {
    totalBays: runs[0]?.bays ?? 0,
  });

  let strataResult: StrataResult = deriveStrata(items, runs, sectionAttr, partitionRules, draft);
  for (const warning of [...partitionRules.warnings, ...strataResult.warnings]) {
    advisories.push(warning);
  }
  // cdt0 is named through the draft so the rule set spells the zones the tree ships;
  // the section stays raw, so a promotion makes a new cdt0 and never a new section.
  const cdt0Of = (item: StudioItem): string =>
    strataResult.cdt0Of.get(item.skuId) ?? namedSectionOf(item, sectionAttr, draft);
  const sectionOf = (item: StudioItem): string => sectionNameOf(item, sectionAttr);
  if (options.cdt0Order?.length) {
    const cdt0OfSku = new Map(items.map((item) => [item.skuId, cdt0Of(item)]));
    walk = walkOrderedByCdt0(walk, (skuId) => cdt0OfSku.get(skuId) ?? "", options.cdt0Order);
  }

  const byCdt0 = new Map<string, StudioItem[]>();
  for (const item of items) {
    const cdt0 = cdt0Of(item);
    (byCdt0.get(cdt0) ?? byCdt0.set(cdt0, []).get(cdt0)!).push(item);
  }

  const attrsSeen = new Set<string>();
  for (const item of items) {
    for (const [attr, value] of Object.entries(item.attrs)) {
      if (attr === sectionAttr || attr === "subCategory") continue;
      if (!isPlaceholderValue(value)) attrsSeen.add(attr);
    }
  }

  // Item-master attributes must win; hierarchy-derived columns would make the tree self-referential.
  const overlayOrder: string[] = [];
  for (const item of items) {
    for (const attr of item.overlayAttrs ?? []) {
      if (!overlayOrder.includes(attr)) overlayOrder.push(attr);
    }
  }
  const candidates =
    overlayOrder.length > 0
      ? overlayOrder.filter((attr) => attrsSeen.has(attr))
      : [...attrsSeen].sort();

  const IDENTITY_LIKE_SHARE = 0.4;
  const identityLikeShareOf = (attr: string): number => {
    const values = items
      .map((item) => item.attrs[attr] ?? null)
      .map((value) => String(value ?? ""));
    const live = values.filter((value) => !isPlaceholderValue(value));
    if (live.length === 0) return 0;
    return new Set(live).size / live.length;
  };
  // Size and identity-like fields cannot form stable merchandise columns.
  const columnCandidates = candidates.filter(
    (attr) =>
      sizeLikeShare(items.map((item) => item.attrs[attr] ?? null)) < SIZE_LIKE_SHARE &&
      identityLikeShareOf(attr) < IDENTITY_LIKE_SHARE,
  );
  const branchScopesOf = (branchItems: readonly StudioItem[]): BlockingScope[] => {
    const byRun = new Map<number, StudioItem[]>();
    for (const item of branchItems) {
      (byRun.get(item.run) ?? byRun.set(item.run, []).get(item.run)!).push(item);
    }
    return [...byRun.entries()].map(([run, scopeItems]) => ({ run, path: [], items: scopeItems }));
  };

  const allScopes = [...byCdt0.values()].flatMap(branchScopesOf);
  const planScores = new Map(
    columnCandidates.map((attr) => [attr, measureBlockingOf(items, attr, runs, allScopes).score]),
  );
  const planColumn = [...planScores.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const BRANCH_COLUMN_MARGIN = 0.15;

  const cdt1AttrOf = new Map<string, string>();

  const scoresOfCdt0 = new Map<string, ReadonlyMap<string, number>>();
  for (const [cdt0, branchItems] of byCdt0) {
    const scopes = branchScopesOf(branchItems);
    const ruledHere = (attr: string): LevelRole | undefined =>
      rulings[`${cdt0} / ${attr}`] ?? rulings[attr];

    // A promotion band already owns this vertical tier and cannot split it again.
    const bandAttr = strataResult.strata.find((entry) => entry.cdt0 === cdt0)?.bandAttr ?? null;
    const usable = columnCandidates.filter((attr) => {
      if (attr === bandAttr) return false;
      if (ruledHere(attr) === "horizontal") return false;
      const values = new Set(
        branchItems
          .map((item) => String(item.attrs[attr] ?? ""))
          .filter((value) => !isPlaceholderValue(value)),
      );
      return values.size > 1;
    });
    if (usable.length === 0) {
      cdt1AttrOf.set(cdt0, "");
      warn("cdt1-empty", `"${cdt0}" has nothing left that splits; cdt1 ships blank there.`);
      continue;
    }
    const ruled = usable.find((attr) => ruledHere(attr) === "vertical");
    const scores = new Map(
      usable.map((attr) => [attr, measureBlockingOf(items, attr, runs, scopes).score]),
    );
    const best = [...scores.entries()].sort((a, b) => b[1] - a[1])[0][0];
    const planUsable = planColumn && usable.includes(planColumn) ? planColumn : undefined;
    const measuredColumn =
      !planUsable || (scores.get(best) ?? 0) - (scores.get(planUsable) ?? 0) >= BRANCH_COLUMN_MARGIN
        ? best
        : planUsable;
    const column = ruled ?? measuredColumn;
    cdt1AttrOf.set(cdt0, column);
    scoresOfCdt0.set(cdt0, scores);
    if ((scores.get(column) ?? 0) < VERTICAL_SCORE) {
      warn(
        "cdt1-weak",
        `"${cdt0}" has no clearly columnar attribute; "${column}" leads at ${(scores.get(column) ?? 0).toFixed(2)}.`,
      );
    }
  }

  const sectionOfCdt0 = new Map(strataResult.strata.map((entry) => [entry.cdt0, entry.section]));
  const byFamily = new Map<string, string[]>();
  for (const cdt0 of byCdt0.keys()) {
    const family = sectionOfCdt0.get(cdt0) ?? cdt0;
    (byFamily.get(family) ?? byFamily.set(family, []).get(family)!).push(cdt0);
  }

  const brandRoleAttr = ((): string | undefined => {
    let best: { attr: string; agreement: number } | undefined;
    for (const attr of columnCandidates) {
      let both = 0;
      let agree = 0;
      for (const item of items) {
        const brand = String(item.attrs[DEFAULT_COLUMN_ATTR] ?? "");
        const value = String(item.attrs[attr] ?? "");
        if (isPlaceholderValue(brand) || isPlaceholderValue(value)) continue;
        both += 1;
        if (sanitizeCdtValue(brand).toLowerCase() === sanitizeCdtValue(value).toLowerCase())
          agree += 1;
      }
      const agreement = both > 0 ? agree / both : 0;
      if (agreement >= 0.9 && (!best || agreement > best.agreement)) best = { attr, agreement };
    }
    return best?.attr;
  })();
  // Related strata share one vocabulary so restrictions stay addressable.
  for (const [family, familyCdt0s] of byFamily) {
    if (familyCdt0s.length < 2) continue;
    const votes = new Map<string, number>();
    let familyItems = 0;
    let bestScore = 0;
    for (const cdt0 of familyCdt0s) {
      const attr = cdt1AttrOf.get(cdt0);
      const size = byCdt0.get(cdt0)?.length ?? 0;
      familyItems += size;
      if (!attr) continue;
      votes.set(attr, (votes.get(attr) ?? 0) + size);

      const reading = measureBlockingOf(items, attr, runs, branchScopesOf(byCdt0.get(cdt0) ?? []));
      const stableColumns = reading.values.filter(
        (value) => value.stability >= VERTICAL_SCORE,
      ).length;
      if (stableColumns >= 2)
        bestScore = Math.max(bestScore, scoresOfCdt0.get(cdt0)?.get(attr) ?? 0);
    }
    const ranked = [...votes.entries()].sort((a, b) => b[1] - a[1]);
    let majority = ranked[0]?.[0];
    if (!majority) continue;
    const margin =
      familyItems > 0 ? ((ranked[0]?.[1] ?? 0) - (ranked[1]?.[1] ?? 0)) / familyItems : 1;
    const nearTie = ranked.length > 1 && margin < BRANCH_COLUMN_MARGIN;
    const nothingColumnar = bestScore < VERTICAL_SCORE;
    if (brandRoleAttr && majority !== brandRoleAttr && (nearTie || nothingColumnar)) {
      advisories.push(
        `"${family}": the family vote is ${nearTie ? `a near tie (${ranked.map(([attr, count]) => `"${attr}" ${count}`).join(" vs ")}, margin ${Math.round(margin * 100)}% < ${BRANCH_COLUMN_MARGIN * 100}%)` : "not close"}${nothingColumnar ? ` and no attribute tiles any stratum as >= 2 stable columns (best structural score ${bestScore.toFixed(2)} < ${VERTICAL_SCORE})` : ""} — rule 16 breaks it to the brand role ("${brandRoleAttr}"), the unit kept whole and addressable by restrictions.`,
      );
      majority = brandRoleAttr;
    }
    for (const cdt0 of familyCdt0s) {
      const chosen = cdt1AttrOf.get(cdt0);
      if (chosen === majority) continue;
      const speaks = (byCdt0.get(cdt0) ?? []).some(
        (item) => !isPlaceholderValue(item.attrs[majority]),
      );
      if (!speaks) continue;

      cdt1AttrOf.set(cdt0, majority);
      if (chosen) {
        advisories.push(
          `"${cdt0}" measured "${chosen}" as its column but conforms to "${family}"'s majority attribute "${majority}" — one segment vocabulary per family.`,
        );
      } else {
        const stale = findings.findIndex(
          (finding) => finding.check === "cdt1-empty" && finding.message.includes(`"${cdt0}"`),
        );
        if (stale >= 0) findings.splice(stale, 1);
      }
    }
  }

  const rawCdt1Of = (item: StudioItem): string => {
    const attr = cdt1AttrOf.get(cdt0Of(item)) ?? "";
    return attr === "" ? "" : sanitizeCdtValue(String(item.attrs[attr] ?? ""));
  };

  const poolOfPair = new Map<string, string>();
  const cdt1Of = (item: StudioItem): string => {
    const raw = rawCdt1Of(item);
    return poolOfPair.get(pairKeyOf(cdt0Of(item), raw)) ?? raw;
  };

  const attrValueOf = resolveAttrValue;
  const shelvesOf = (skuItems: readonly StudioItem[]): number[] =>
    [
      ...new Set(skuItems.flatMap((item) => item.placements.map((placement) => placement.shelf))),
    ].sort((a, b) => a - b);
  const rowOf = (item: StudioItem): number | undefined =>
    walk.firstPlacement.get(item.skuId)?.shelf;
  const kidItemsIn = (
    nodeItems: readonly StudioItem[],
    node: TreeNode,
    value: string,
  ): StudioItem[] => nodeItems.filter((item) => attrValueOf(item, node.attr) === value);

  const isRowPartition = (node: TreeNode, nodeItems: readonly StudioItem[]): boolean => {
    if (node.reading !== "vertical") return false;
    const blocks = node.kids
      .filter((kid) => kid.count >= 2)
      .map((kid) => new Set(kidItemsIn(nodeItems, node, kid.value).map(rowOf)));
    if (blocks.length < 2) return false;
    const seen = new Set<number | undefined>();
    for (const shelves of blocks) {
      for (const shelf of shelves) {
        if (seen.has(shelf)) return false;
        seen.add(shelf);
      }
    }
    return true;
  };

  const nodeCandidatesOf = (cdt0: string): string[] => {
    const cdt1Attr = cdt1AttrOf.get(cdt0) ?? "";
    const bandAttr = strataResult.strata.find((entry) => entry.cdt0 === cdt0)?.bandAttr;
    return candidates.filter((attr) => attr !== cdt1Attr && attr !== bandAttr);
  };
  const columnsOf = (branchItems: readonly StudioItem[]): Map<string, StudioItem[]> => {
    const columns = new Map<string, StudioItem[]>();
    for (const item of branchItems) {
      const value = cdt1Of(item);
      (columns.get(value) ?? columns.set(value, []).get(value)!).push(item);
    }
    return columns;
  };
  const rowHomeKeyOf = (rule: Pick<ReplicaRowHome, "cdt0" | "cdt1" | "attr" | "value">): string =>
    [rule.cdt0, rule.cdt1, rule.attr, rule.value].join("\u0000");

  const magnitudeOf = (value: string): number | null => {
    if (value === NONE_VALUE) return null;
    const match = value.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : null;
  };
  const cutBaseOfAttr = (attr: string): string | null => {
    const index = attr.indexOf(" >= ");
    return index < 0 ? null : attr.slice(0, index);
  };
  const rowsKeyOf = (skuItems: readonly StudioItem[]): string =>
    [...new Set(skuItems.map(rowOf))].sort((a, b) => (a ?? 0) - (b ?? 0)).join(",");
  interface Seed {
    baseAttr: string;
    numeric: boolean;

    sideRows: readonly string[];

    sideValues: readonly (readonly string[])[];
  }

  const conformTo = (seed: Seed, nodeItems: readonly StudioItem[]): StudioItem[][] | null => {
    if (seed.numeric) {
      if (seed.sideRows.length !== 2) return null;
      const withMagnitude = nodeItems.map((item) => ({
        item,
        magnitude: magnitudeOf(attrValueOf(item, seed.baseAttr)),
      }));
      if (withMagnitude.some((entry) => entry.magnitude === null)) return null;
      const distinct = [...new Set(withMagnitude.map((entry) => entry.magnitude!))].sort(
        (a, b) => a - b,
      );
      for (const threshold of distinct.slice(1)) {
        const small = withMagnitude
          .filter((entry) => entry.magnitude! < threshold)
          .map((entry) => entry.item);
        const large = withMagnitude
          .filter((entry) => entry.magnitude! >= threshold)
          .map((entry) => entry.item);
        if (small.length < 2 || large.length < 2) continue;
        if (rowsKeyOf(small) === seed.sideRows[0] && rowsKeyOf(large) === seed.sideRows[1])
          return [small, large];
      }
      return null;
    }
    const sides: StudioItem[][] = seed.sideRows.map(() => []);
    const byValue = new Map<string, StudioItem[]>();
    for (const item of nodeItems) {
      const value = attrValueOf(item, seed.baseAttr);
      (byValue.get(value) ?? byValue.set(value, []).get(value)!).push(item);
    }
    for (const [value, valueItems] of byValue) {
      const side = seed.sideValues.findIndex((values) => values.includes(value));
      if (side < 0) return null;
      const rows = new Set(valueItems.map(rowOf));
      const sideRows = new Set(seed.sideRows[side].split(",").map(Number));
      if (![...rows].every((row) => row !== undefined && sideRows.has(row))) return null;
      sides[side].push(...valueItems);
    }
    if (sides.some((sideItems) => sideItems.length < 2)) return null;
    return sides;
  };

  const MAJORITY_SHARE = 0.5;
  const widthOfItems = (skuItems: readonly StudioItem[]): number =>
    skuItems.reduce((sum, item) => sum + item.placements.reduce((s, p) => s + p.widthCm, 0), 0);
  const preRowHomes: ReplicaRowHome[] = [];
  const privateRowHomes: ReplicaRowHome[] = [];
  const privateRowHomeKeys = new Set<string>();
  const groupOfRule = new Map<ReplicaRowHome, string>();
  for (const [cdt0, branchItems] of byCdt0) {
    const nodeCandidates = nodeCandidatesOf(cdt0);
    const nodes = [...columnsOf(branchItems)];

    {
      const cdt1Attr = cdt1AttrOf.get(cdt0) ?? "";
      const rowAttrs = [...new Set([cdt1Attr, ...columnCandidates])].filter((attr) => attr !== "");
      let carvedByRow = false;
      for (const attr of rowAttrs) {
        const byValue = new Map<string, StudioItem[]>();
        for (const item of branchItems) {
          const value = attrValueOf(item, attr);
          (byValue.get(value) ?? byValue.set(value, []).get(value)!).push(item);
        }
        if (byValue.size < 2) continue;
        const perValue = [...byValue.entries()].map(([value, valueItems]) => {
          const byRow = new Map<number, StudioItem[]>();
          for (const item of valueItems) {
            const row = rowOf(item);
            if (row === undefined) continue;
            (byRow.get(row) ?? byRow.set(row, []).get(row)!).push(item);
          }
          return { value, valueItems, byRow };
        });

        if (
          perValue.some(
            (entry) =>
              [...entry.byRow.values()].filter((rowItems) => rowItems.length >= 2).length >= 2,
          )
        )
          continue;
        const singleRowValues = perValue.filter((entry) => entry.byRow.size === 1);
        const rowsOfSingle = new Set(singleRowValues.map((entry) => [...entry.byRow.keys()][0]));
        const singleShare =
          widthOfItems(branchItems) > 0
            ? widthOfItems(singleRowValues.flatMap((entry) => entry.valueItems)) /
              widthOfItems(branchItems)
            : 0;
        if (rowsOfSingle.size < 2) continue;
        if (singleShare < MAJORITY_SHARE) {
          advisories.push(
            `"${cdt0}": ${attr} values sit on separate rows (${[...rowsOfSingle].sort((a, b) => a - b).join(", ")}) but the single-row values hold ${Math.round(singleShare * 100)}% of the width (majority floor ${MAJORITY_SHARE * 100}%) — not carved by row on ${attr}.`,
          );
          continue;
        }

        const byCdt1Row = new Map<string, { cdt1: string; row: number; items: StudioItem[] }>();
        for (const item of branchItems) {
          const row = rowOf(item);
          if (row === undefined) continue;
          const cdt1 = cdt1Of(item);
          const key = `${cdt1}\u0000${row}`;
          (byCdt1Row.get(key) ?? byCdt1Row.set(key, { cdt1, row, items: [] }).get(key)!).items.push(
            item,
          );
        }
        for (const { cdt1, row, items: rowItems } of byCdt1Row.values()) {
          const rule: ReplicaRowHome = {
            cdt0,
            cdt1,
            attr: `${attr} (rows)`,
            value: `row ${row}`,
            shelves: [row],
            skuIds: rowItems.map((item) => item.skuId),
            shipped: carveByRowAttribute,
          };
          if (carveByRowAttribute) {
            preRowHomes.push(rule);
            groupOfRule.set(rule, `${cdt0} / rows`);
          } else if (!privateRowHomeKeys.has(rowHomeKeyOf(rule))) {
            privateRowHomeKeys.add(rowHomeKeyOf(rule));
            privateRowHomes.push(rule);
          }
        }
        const spread = perValue.filter((entry) => entry.byRow.size > 1);
        const fact = `"${cdt0}" is partitioned by row on ${attr}${attr === cdt1Attr ? " (its cdt1)" : ` (cdt1 is ${cdt1Attr})`}: ${singleRowValues.map((entry) => `"${entry.value}" row ${[...entry.byRow.keys()][0]}`).join(", ")} (${Math.round(singleShare * 100)}% of the width)${spread.length > 0 ? `; ${spread.map((entry) => `"${entry.value}"`).join(", ")} spread over rows` : ""}`;
        advisories.push(
          carveByRowAttribute
            ? `${fact} — carved into one cdt0 sub-stratum per row (§9: a vertical fact above cdt1 goes to cdt0).`
            : `${fact} — NOT carved (a cdt0 is never carved by its own cdt1; slotting variants by row name would stop products moving between shelves): the band ships whole with its brand columns inside, the fact is recorded in rowHomes (shipped: false).`,
        );
        carvedByRow = carveByRowAttribute;
        break;
      }
      if (carvedByRow) continue;
    }

    interface RootPartition {
      cdt1: string;
      node: TreeNode;
      items: readonly StudioItem[];
      seedKey: string;
    }
    const seeds = new Map<string, Seed>();
    const rootPartitions: RootPartition[] = [];
    for (const [cdt1, columnItems] of nodes) {
      const tree = inferTree(columnItems, runs, nodeCandidates, attrValueOf);
      if (!tree || !isRowPartition(tree, columnItems)) continue;
      const baseAttr = cutBaseOfAttr(tree.attr) ?? tree.attr;
      const blocks = tree.kids
        .filter((kid) => kid.count >= 2)
        .map((kid) => ({ value: kid.value, items: kidItemsIn(columnItems, tree, kid.value) }));
      const numeric =
        cutBaseOfAttr(tree.attr) !== null ||
        blocks.every((block) => magnitudeOf(block.value) !== null);
      const sides = numeric
        ? [...blocks].sort(
            (a, b) =>
              Math.min(...a.items.map((item) => magnitudeOf(attrValueOf(item, baseAttr)) ?? 0)) -
              Math.min(...b.items.map((item) => magnitudeOf(attrValueOf(item, baseAttr)) ?? 0)),
          )
        : blocks;
      const seed: Seed = {
        baseAttr,
        numeric,
        sideRows: sides.map((side) => rowsKeyOf(side.items)),
        sideValues: sides.map((side) => [side.value]),
      };
      const seedKey = [
        baseAttr,
        numeric ? "numeric" : seed.sideValues.map((values) => values.join("/")).join("|"),
        seed.sideRows.join("|"),
      ].join("\u0000");
      seeds.set(seedKey, seed);
      rootPartitions.push({ cdt1, node: tree, items: columnItems, seedKey });
    }
    const sharedSeeds = new Set<string>();
    for (const [seedKey, seed] of seeds) {
      const conforming = nodes
        .map(([cdt1, nodeItems]) => ({ cdt1, sides: conformTo(seed, nodeItems) }))
        .filter((entry): entry is { cdt1: string; sides: StudioItem[][] } => entry.sides !== null);
      if (conforming.length < 2) continue;

      const cdt0Width = widthOfItems(branchItems);
      const conformingWidth = conforming.reduce(
        (sum, entry) => sum + widthOfItems(nodes.find(([cdt1]) => cdt1 === entry.cdt1)?.[1] ?? []),
        0,
      );
      const conformingShare = cdt0Width > 0 ? conformingWidth / cdt0Width : 0;
      // A minority pattern stays node-local instead of reshaping the whole stratum.
      if (conformingShare < MAJORITY_SHARE) {
        advisories.push(
          `"${cdt0}": ${conforming.map((entry) => `"${entry.cdt1}"`).join(", ")} share a row partition on ${seed.baseAttr}, but they hold ${Math.round(conformingShare * 100)}% of the stratum's width (majority floor ${MAJORITY_SHARE * 100}%) — a minority rule, not carved; each stays in its node, folded into cdt3 (lossy).`,
        );
        continue;
      }
      sharedSeeds.add(seedKey);
      const sideLabel = (index: number): string =>
        seed.numeric ? (index === 0 ? "small" : "large") : seed.sideValues[index].join("/");

      const attr = seed.numeric ? `${seed.baseAttr} (cut)` : seed.baseAttr;

      const sideRowSets = seed.sideRows.map((key) => new Set(key.split(",").map(Number)));
      const joinedByRow: string[] = [];
      let leftBehind = 0;
      for (const [cdt1, nodeItems] of nodes) {
        let sides = conforming.find((entry) => entry.cdt1 === cdt1)?.sides ?? null;
        if (!sides) {
          sides = sideRowSets.map(() => []);
          for (const item of nodeItems) {
            const row = rowOf(item);
            const side = sideRowSets.findIndex((rows) => row !== undefined && rows.has(row));
            if (side < 0) leftBehind += 1;
            else sides[side].push(item);
          }
          joinedByRow.push(cdt1);
        }
        sides.forEach((sideItems, index) => {
          if (sideItems.length === 0) return;
          const rule: ReplicaRowHome = {
            cdt0,
            cdt1,
            attr,
            value: sideLabel(index),
            shelves: seed.sideRows[index].split(",").map(Number),
            skuIds: sideItems.map((item) => item.skuId),
            shipped: true,
          };
          preRowHomes.push(rule);
          groupOfRule.set(rule, `${cdt0} / ${seedKey}`);
        });
      }
      advisories.push(
        `"${cdt0}" shares a row partition on ${seed.baseAttr}${seed.numeric ? " (each brand at its own threshold)" : ""}: ${conforming.map((entry) => `"${entry.cdt1}"`).join(", ")} follow it (${Math.round(conformingShare * 100)}% of the width)${joinedByRow.length > 0 ? `; ${joinedByRow.map((cdt1) => `"${cdt1}"`).join(", ")} join by the row their items sit on` : ""}${leftBehind > 0 ? `; ${leftBehind} SKU(s) on rows outside the partition stay in "${cdt0}"` : ""} — concatenated upward into one cdt0 sub-stratum per row side (§9: shared by the zone's brands).`,
      );
    }
    for (const partition of rootPartitions) {
      if (sharedSeeds.has(partition.seedKey)) continue;
      for (const kid of partition.node.kids) {
        const kidItems = kidItemsIn(partition.items, partition.node, kid.value);
        const rule: ReplicaRowHome = {
          cdt0,
          cdt1: partition.cdt1,
          attr: partition.node.attr,
          value: kid.value,
          shelves: shelvesOf(kidItems),
          skuIds: kidItems.map((item) => item.skuId),
          shipped: false,
        };
        privateRowHomes.push(rule);
        privateRowHomeKeys.add(rowHomeKeyOf(rule));
      }
    }
  }
  const preHomed = withRowHomeStrata(strataResult, preRowHomes);
  for (const warning of preHomed.rowHomeWarnings) advisories.push(warning);

  const parentOfSubStratum = new Map<string, string>();
  const nodeOfSubStratum = new Map<string, string>();
  for (const stratum of preHomed.rowHomeStrata) {
    const rule = preRowHomes.find((entry) => entry.skuIds.includes(stratum.skuIds[0] ?? ""));
    if (rule) {
      parentOfSubStratum.set(stratum.cdt0, rule.cdt0);
      nodeOfSubStratum.set(stratum.cdt0, groupOfRule.get(rule) ?? `${rule.cdt0} / ${rule.cdt1}`);
    }
  }

  const emptied = preHomed.strata
    .filter((stratum) => stratum.skuIds.length === 0)
    .map((stratum) => stratum.cdt0);
  for (const cdt0 of emptied) {
    advisories.push(
      `"${cdt0}" ships no item of its own once its row partition is carved; no rows are emitted for it.`,
    );
  }
  strataResult = {
    ...preHomed,
    strata: preHomed.strata.filter((stratum) => stratum.skuIds.length > 0),
  };
  byCdt0.clear();
  for (const item of items) {
    const cdt0 = cdt0Of(item);
    (byCdt0.get(cdt0) ?? byCdt0.set(cdt0, []).get(cdt0)!).push(item);
  }
  for (const [subCdt0, parentCdt0] of parentOfSubStratum) {
    cdt1AttrOf.set(subCdt0, cdt1AttrOf.get(parentCdt0) ?? "");
  }

  const firstRankOfCdt0 = new Map<string, number>();
  for (const item of items) {
    const cdt0 = cdt0Of(item);
    const rank = walk.rankOf.get(item.skuId) ?? Number.MAX_SAFE_INTEGER;
    if (rank < (firstRankOfCdt0.get(cdt0) ?? Number.MAX_SAFE_INTEGER))
      firstRankOfCdt0.set(cdt0, rank);
  }
  const firstRankOfNode = new Map<string, number>();
  for (const [subCdt0, node] of nodeOfSubStratum) {
    const rank = firstRankOfCdt0.get(subCdt0) ?? Number.MAX_SAFE_INTEGER;
    if (rank < (firstRankOfNode.get(node) ?? Number.MAX_SAFE_INTEGER))
      firstRankOfNode.set(node, rank);
  }
  const priorityRankOf = (cdt0: string): readonly number[] => {
    const own = firstRankOfCdt0.get(cdt0) ?? Number.MAX_SAFE_INTEGER;
    const node = nodeOfSubStratum.get(cdt0);
    return [node ? (firstRankOfNode.get(node) ?? own) : own, own];
  };
  const requestedOrder = [...new Set(options.cdt0Order ?? [])];
  const requestedCdt0s = new Set(requestedOrder);
  const measuredCdt0s = new Set(strataResult.cdt0Order);
  const rootOrder = [
    ...requestedOrder.filter((cdt0) => measuredCdt0s.has(cdt0)),
    ...strataResult.cdt0Order.filter((cdt0) => !requestedCdt0s.has(cdt0)),
  ];
  const clusters: string[][] = [];
  for (const cdt0 of rootOrder) {
    const parent = parentOfSubStratum.get(cdt0);
    const cluster = parent ? clusters.find((entry) => entry[0] === parent) : undefined;
    if (cluster) cluster.push(cdt0);
    else clusters.push([cdt0]);
  }
  const priorityCdt0Order = options.cdt0Order?.length
    ? rootOrder
    : clusters.flatMap((cluster) =>
        [...cluster].sort((a, b) => {
          const [nodeA, ownA] = priorityRankOf(a);
          const [nodeB, ownB] = priorityRankOf(b);
          return nodeA - nodeB || ownA - ownB;
        }),
      );

  const rankIn = (skuIds: readonly string[]): number =>
    Math.min(...skuIds.map((skuId) => walk.rankOf.get(skuId) ?? Number.MAX_SAFE_INTEGER));

  // Sub-floor columns pool so Pogger can allocate them a viable width.
  const SUB_FLOOR_SHARE = 0.4;
  const POOL_CAP = 6;
  const TOP_SALES_EXEMPT = 2;
  const pools: Record<string, ReplicaPool> = {};
  const poolViability: ReplicaColumnViability[] = [];
  {
    const widthOfColumn = (colItems: readonly StudioItem[]): number =>
      colItems.reduce((sum, item) => sum + item.placements.reduce((s, p) => s + p.widthCm, 0), 0);
    const salesOfColumn = (colItems: readonly StudioItem[]): number =>
      colItems.reduce((sum, item) => sum + (item.salesAmount ?? 0), 0);
    interface Viability {
      cdt0: string;
      cdt1: string;
      subFloor: boolean;
      exempt: boolean;
      widthShare: number;
      salesShare: number;
      widthCm: number;
      items: StudioItem[];
    }
    const pairViability = new Map<string, Viability>();
    const dominantOf = new Map<string, string>();
    for (const [cdt0, branchItems] of byCdt0) {
      const cols = new Map<string, StudioItem[]>();
      for (const item of branchItems) {
        const value = cdt1Of(item);
        (cols.get(value) ?? cols.set(value, []).get(value)!).push(item);
      }
      const widths = [...cols.entries()].map(([cdt1, colItems]) => ({
        cdt1,
        colItems,
        width: widthOfColumn(colItems),
        sales: salesOfColumn(colItems),
      }));
      const meanWidth = widths.length
        ? widths.reduce((sum, entry) => sum + entry.width, 0) / widths.length
        : 0;

      const anySales = widths.some((entry) => entry.sales > 0);
      const bySales = [...widths].sort((a, b) =>
        anySales ? b.sales - a.sales : b.width - a.width,
      );
      const exempt = new Set(bySales.slice(0, TOP_SALES_EXEMPT).map((entry) => entry.cdt1));
      if (bySales.length > 0) dominantOf.set(cdt0, bySales[0].cdt1);
      const totalSales = widths.reduce((sum, entry) => sum + entry.sales, 0);
      for (const { cdt1, colItems, width, sales } of widths) {
        const oneSkuColumn = colItems.length === 1;
        pairViability.set(pairKeyOf(cdt0, cdt1), {
          cdt0,
          cdt1,
          subFloor: (meanWidth > 0 && width < meanWidth * SUB_FLOOR_SHARE) || oneSkuColumn,
          exempt: exempt.has(cdt1),
          widthShare: meanWidth > 0 ? width / meanWidth : 1,
          salesShare: totalSales > 0 ? sales / totalSales : 0,
          widthCm: width,
          items: colItems,
        });
      }
    }

    const poolable = (viability: Viability): boolean => viability.subFloor && !viability.exempt;
    for (const viability of pairViability.values()) {
      poolViability.push({
        cdt0: viability.cdt0,
        cdt1: viability.cdt1,
        items: viability.items.length,
        widthCm: Math.round(viability.widthCm * 10) / 10,
        widthShare: Math.round(viability.widthShare * 100) / 100,
        salesShare: Math.round(viability.salesShare * 1000) / 1000,
        subFloor: viability.subFloor,
        exempt: viability.exempt,
        poolable: poolable(viability),
      });
    }
    const initialsOf = (name: string): string =>
      name
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word[0].toUpperCase())
        .join("");
    const poolNameOf = (cdt0: string, index: number, count: number): string => {
      const suffix = count > 1 ? ` pool ${index + 1}` : " pool";
      const full = sanitizeCdtValue(`${cdt0}${suffix}`);
      return full.length <= CDT0_NAME_BUDGET
        ? full
        : sanitizeCdtValue(`${initialsOf(cdt0)}${suffix}`);
    };
    for (const cdt0 of byCdt0.keys()) {
      const candidates = [...pairViability.values()]
        .filter((viability) => viability.cdt0 === cdt0 && viability.subFloor && !viability.exempt)
        .filter(poolable)
        .sort(
          (a, b) =>
            rankIn(a.items.map((item) => item.skuId)) - rankIn(b.items.map((item) => item.skuId)),
        );
      if (candidates.length === 0) continue;
      const host = dominantOf.get(cdt0);
      if (candidates.length === 1 && host && host !== candidates[0].cdt1) {
        const guest = candidates[0];
        const hostColumn = pairViability.get(pairKeyOf(cdt0, host));
        pools[pairKeyOf(cdt0, host)] = {
          cdt0,
          cdt1: host,
          host,
          members: [
            {
              cdt1: host,
              skuIds: hostColumn?.items.map((item) => item.skuId) ?? [],
              widthShare: Math.round((hostColumn?.widthShare ?? 1) * 100) / 100,
              reason: "host: the stratum's dominant brand keeps its own column and labels",
            },
            {
              cdt1: guest.cdt1,
              skuIds: guest.items.map((item) => item.skuId),
              widthShare: Math.round(guest.widthShare * 100) / 100,
              reason: `${guest.items.length === 1 ? "one-SKU column; " : ""}${Math.round(guest.widthShare * 100)}% of "${cdt0}"'s mean column width (floor ${SUB_FLOOR_SHARE * 100}%); the only sub-floor brand here, so it joins "${host}" as its own block`,
            },
          ],
        };
        poolOfPair.set(pairKeyOf(cdt0, guest.cdt1), host);
        advisories.push(
          `"${cdt0}" seats "${guest.cdt1}" inside "${host}" as its own block (rule 2: a lone sub-floor brand cannot stand as a one-brand stratum).`,
        );
        continue;
      }
      if (candidates.length < 2) continue;
      const poolCount = Math.ceil(candidates.length / POOL_CAP);
      const poolSize = Math.ceil(candidates.length / poolCount);
      for (let index = 0; index < poolCount; index += 1) {
        const members = candidates.slice(index * poolSize, (index + 1) * poolSize);
        const name = poolNameOf(cdt0, index, poolCount);
        pools[name] = {
          cdt0,
          cdt1: name,
          members: members.map((member) => ({
            cdt1: member.cdt1,
            skuIds: member.items.map((item) => item.skuId),
            widthShare: Math.round(member.widthShare * 100) / 100,
            reason: `${member.items.length === 1 ? "one-SKU column; " : ""}${Math.round(member.widthShare * 100)}% of "${cdt0}"'s mean column width (floor ${SUB_FLOOR_SHARE * 100}%); sub-floor in "${cdt0}"`,
          })),
        };
        for (const member of members) poolOfPair.set(pairKeyOf(cdt0, member.cdt1), name);
        advisories.push(
          `"${cdt0}" pools ${members.map((member) => `"${member.cdt1}"`).join(", ")} into cdt1 "${name}" (each under ${SUB_FLOOR_SHARE * 100}% of the mean column width here; members stay whole, in wall order, brand-prefixed inside).`,
        );
      }
    }
  }

  {
    const stratumOf = new Map(strataResult.strata.map((stratum) => [stratum.cdt0, stratum]));
    const itemById = new Map(items.map((item) => [item.skuId, item]));
    const itemsOfStratum = (cdt0: string): StudioItem[] =>
      (stratumOf.get(cdt0)?.skuIds ?? [])
        .map((skuId) => itemById.get(skuId))
        .filter((item): item is StudioItem => !!item);
    const extentOf = (
      skuItems: readonly StudioItem[],
    ): { min: number; max: number; centre: number } => {
      let min = Number.POSITIVE_INFINITY;
      let max = Number.NEGATIVE_INFINITY;
      let weighted = 0;
      let weight = 0;
      for (const item of skuItems) {
        for (const placement of item.placements) {
          min = Math.min(min, placement.xCm);
          max = Math.max(max, placement.xCm + placement.widthCm);
          weighted += (placement.xCm + placement.widthCm / 2) * placement.widthCm;
          weight += placement.widthCm;
        }
      }
      return { min, max, centre: weight > 0 ? weighted / weight : (min + max) / 2 };
    };
    const familyOf = (cdt0: string): string => stratumOf.get(cdt0)?.section ?? cdt0;
    const families = new Map<string, string[]>();
    for (const cdt0 of priorityCdt0Order) {
      if (!stratumOf.has(cdt0)) continue;
      const family = familyOf(cdt0);
      (families.get(family) ?? families.set(family, []).get(family)!).push(cdt0);
    }
    for (const [family, familyCdt0s] of families) {
      if (familyCdt0s.length < 2) continue;
      const rowsOf = (cdt0: string) => new Set(stratumOf.get(cdt0)?.homeShelves ?? []);
      const widthOf = (cdt0: string) => widthOfItems(itemsOfStratum(cdt0));
      const familyExtent = extentOf(familyCdt0s.flatMap(itemsOfStratum));
      const leftEdge: string[] = [];
      const rightEdge: string[] = [];
      for (const cdt0 of familyCdt0s) {
        const rows = rowsOf(cdt0);
        if (rows.size === 0 || itemsOfStratum(cdt0).length === 0) continue;
        const others = familyCdt0s.filter(
          (other) => other !== cdt0 && itemsOfStratum(other).length > 0,
        );
        const coverers = others.filter((other) => [...rowsOf(other)].some((row) => rows.has(row)));
        const covered = [...rows].every((row) => coverers.some((other) => rowsOf(other).has(row)));
        if (!covered || coverers.length === 0) continue;
        if (!coverers.every((other) => widthOf(cdt0) < widthOf(other))) continue;
        const centre = extentOf(itemsOfStratum(cdt0)).centre;
        const left = centre < (familyExtent.min + familyExtent.max) / 2;
        (left ? leftEdge : rightEdge).push(cdt0);
        advisories.push(
          `"${cdt0}" is a band nested inside ${coverers.map((other) => `"${other}"`).join(" + ")}'s rows (home ${[...rows].sort((a, b) => a - b).join(",")}, ${Math.round(widthOf(cdt0))} cm, x-centre ${Math.round(centre)} in the ${left ? "left" : "right"} half of "${family}"'s ${Math.round(familyExtent.min)}–${Math.round(familyExtent.max)} cm): its pairs take the family's ${left ? "leading" : "trailing"} edge so no other band continues past it on a shared row.`,
        );
      }
      if (leftEdge.length === 0 && rightEdge.length === 0) continue;
      const middle = familyCdt0s.filter(
        (cdt0) => !leftEdge.includes(cdt0) && !rightEdge.includes(cdt0),
      );
      const reordered = [...leftEdge, ...middle, ...rightEdge];
      const slots = priorityCdt0Order
        .map((cdt0, index) => (familyCdt0s.includes(cdt0) ? index : -1))
        .filter((index) => index >= 0);
      slots.forEach((slot, index) => {
        priorityCdt0Order[slot] = reordered[index];
      });
    }
  }

  const pairs = pairPriorities(items, walk, cdt0Of, cdt1Of, priorityCdt0Order, sectionOf);

  const treeItems: Record<string, ReplicaTreeItem> = {};
  const ruleReports: ReplicaOrderingRuleReport[] = [];
  const rowHomes: ReplicaRowHome[] = [];

  const ruleNodes: RuleNode[] = [];

  for (const cdt0 of pairs.cdt0Order) {
    const branchItems = byCdt0.get(cdt0) ?? [];
    const nodeCandidates = nodeCandidatesOf(cdt0);

    const nodeColumns: { cdt1: string; columnItems: StudioItem[]; brandPrefix: string | null }[] =
      [];
    for (const [cdt1, columnItems] of columnsOf(branchItems)) {
      const pool = Object.values(pools).find((entry) => entry.cdt0 === cdt0 && entry.cdt1 === cdt1);
      if (!pool) {
        nodeColumns.push({ cdt1, columnItems, brandPrefix: null });
        continue;
      }
      const byMember = new Map<string, StudioItem[]>();
      for (const item of columnItems) {
        const brand = rawCdt1Of(item);
        (byMember.get(brand) ?? byMember.set(brand, []).get(brand)!).push(item);
      }
      for (const [brand, memberItems] of [...byMember.entries()].sort(
        (a, b) => rankIn(a[1].map((item) => item.skuId)) - rankIn(b[1].map((item) => item.skuId)),
      )) {
        nodeColumns.push({
          cdt1,
          columnItems: memberItems,
          brandPrefix: brand === pool.host ? null : brand,
        });
      }
    }

    for (const { cdt1, columnItems, brandPrefix } of nodeColumns) {
      const tree = inferTree(columnItems, runs, nodeCandidates, attrValueOf);
      if (!tree) {
        advisories.push(
          `"${cdt0} › ${cdt1}" has no attribute that splits on the wall; one unnamed block.`,
        );
      }

      const itemsOfNode = new Map<TreeNode, StudioItem[]>();
      const assign = (node: TreeNode | null, nodeItems: StudioItem[]): void => {
        if (!node) return;
        itemsOfNode.set(node, nodeItems);
        for (const kid of node.kids) {
          assign(
            kid.node,
            nodeItems.filter((item) => attrValueOf(item, node.attr) === kid.value),
          );
        }
      };
      assign(tree, columnItems);
      const kidItemsOf = (node: TreeNode, value: string): StudioItem[] =>
        kidItemsIn(itemsOfNode.get(node) ?? [], node, value);

      const multiRowBlock = (skuItems: readonly StudioItem[]): boolean => {
        const perShelf = new Map<number, number>();
        for (const item of skuItems) {
          const shelf = rowOf(item);
          if (shelf !== undefined) perShelf.set(shelf, (perShelf.get(shelf) ?? 0) + 1);
        }
        return [...perShelf.values()].filter((count) => count >= 2).length >= 2;
      };
      const windowSplit = (node: TreeNode): boolean => {
        const multi = node.kids.filter(
          (kid) => kid.count >= 2 && multiRowBlock(kidItemsOf(node, kid.value)),
        ).length;
        if (multi === 0) return false;

        return !(node.kids.length === 2 && multi === 1);
      };

      const rowPartition = (node: TreeNode): boolean =>
        isRowPartition(node, itemsOfNode.get(node) ?? []);

      const partsCache = new Map<TreeNode, Map<string, string | null>>();
      const partsOverKids = (kids: TreeNode["kids"]): Map<string, string | null> => {
        const parts = new Map<string, string | null>();
        if (kids.every((kid) => kid.count <= 1)) {
          for (const kid of kids) parts.set(kid.value, null);
        } else {
          let index = 0;
          while (index < kids.length) {
            if (kids[index].count >= 2) {
              parts.set(kids[index].value, kids[index].value);
              index += 1;
              continue;
            }
            let end = index;
            while (end < kids.length && kids[end].count <= 1) end += 1;
            const run = kids.slice(index, end);
            const live = run.map((kid) => kid.value).filter((value) => value !== NONE_VALUE);
            const part = run.length === 1 ? run[0].value : live.length > 0 ? live.join("/") : null;
            for (const kid of run) parts.set(kid.value, part);
            index = end;
          }
        }
        return parts;
      };
      const cdt3PartsOf = (node: TreeNode): Map<string, string | null> => {
        const cached = partsCache.get(node);
        if (cached) return cached;
        const parts = partsOverKids(node.kids);
        partsCache.set(node, parts);
        return parts;
      };
      const reported = new Set<TreeNode>();
      const describeNode = (node: TreeNode): string =>
        `${node.attr} [${node.kind} ${node.reading}${node.direction} ${(node.purity * 100).toFixed(0)}% pure, ${node.gain.toFixed(2)} bits]`;

      interface ItemPath {
        cdt2Parts: string[];
        cdt2Attrs: string[];
        cdt3Parts: string[];
        cdt3Nodes: TreeNode[];
        steps: ReplicaPathStep[];
      }

      const isLadder = (node: TreeNode): boolean =>
        node.direction !== "" && cutBaseOfAttr(node.attr) === null;
      const singleRow = new Set(columnItems.map(rowOf)).size <= 1;
      const allNodes: TreeNode[] = [];
      const collect = (node: TreeNode | null): void => {
        if (!node) return;
        allNodes.push(node);
        for (const kid of node.kids) collect(kid.node);
      };
      collect(tree);
      const hasWindow =
        !singleRow &&
        allNodes.some(
          (node) =>
            !(node.reading === "vertical" && rowPartition(node)) &&
            windowSplit(node) &&
            !isLadder(node),
        );
      const cdt2Mode: "root" | "windows" | "first" = singleRow
        ? "root"
        : hasWindow
          ? "windows"
          : "first";

      const ladderGroups = new Map<TreeNode, Map<string, string>>();
      const ladderGroupPart = (node: TreeNode, value: string): string => {
        let groups = ladderGroups.get(node);
        if (!groups) {
          groups = new Map();
          const rungs = node.kids.map((kid) => {
            const rows = kidItemsOf(node, kid.value)
              .map(rowOf)
              .filter((row): row is number => row !== undefined);
            return {
              value: kid.value,
              row: rows.length > 0 ? Math.min(...rows) : Number.MAX_SAFE_INTEGER,
            };
          });
          let start = 0;
          for (let index = 1; index <= rungs.length; index += 1) {
            if (index === rungs.length || rungs[index].row !== rungs[start].row) {
              const run = rungs.slice(start, index);
              const label =
                run.length === 1 ? run[0].value : `${run[0].value}-${run[run.length - 1].value}`;
              for (const rung of run) groups.set(rung.value, label);
              start = index;
            }
          }
          ladderGroups.set(node, groups);
        }
        return groups.get(value) ?? value;
      };

      const ruleTreeOf = (
        node: TreeNode | null,
        phase: "top" | "cdt2" | "cdt3",
      ): RuleTreeNode | null => {
        if (!node) return null;
        const partition = node.reading === "vertical" && rowPartition(node);
        let carrier: RuleCarrier;
        let next = phase;
        let ladderGroups: Record<string, string> | undefined;
        if (partition) {
          carrier = phase === "top" ? "rowHome" : "lossy";
          if (phase !== "top") next = "cdt3";
        } else {
          const takeAsCdt2 =
            cdt2Mode === "windows"
              ? phase !== "cdt3" && windowSplit(node) && !isLadder(node)
              : phase === "top";
          if (takeAsCdt2 && isLadder(node) && !singleRow) {
            carrier = "ladderCdt2";
            ladderGroups = Object.fromEntries(
              node.kids.map((kid) => [kid.value, ladderGroupPart(node, kid.value)]),
            );
            next = "cdt3";
          } else if (takeAsCdt2) {
            carrier = "cdt2";
            next = "cdt2";
          } else {
            carrier = "cdt3";
            next = "cdt3";
          }
        }
        return {
          attr: node.attr,
          carrier,
          describe: describeNode(node),
          direction: node.direction,
          ...(ladderGroups ? { ladderGroups } : {}),
          kids: node.kids.map((kid) => ({
            value: kid.value,
            count: kid.count,
            node: ruleTreeOf(kid.node, next),
          })),
        };
      };
      ruleNodes.push({ cdt0, cdt1: brandPrefix ?? cdt1, tree: ruleTreeOf(tree, "top") });

      let pathBreakReported = false;
      const pathOf = (item: StudioItem): ItemPath => {
        const path: ItemPath = {
          cdt2Parts: brandPrefix !== null ? [brandPrefix] : [],
          cdt2Attrs: brandPrefix !== null ? [cdt1AttrOf.get(cdt0) ?? "brand"] : [],
          cdt3Parts: [],
          cdt3Nodes: [],
          steps:
            brandPrefix !== null
              ? [
                  {
                    attr: cdt1AttrOf.get(cdt0) ?? "brand",
                    value: attrValueOf(item, cdt1AttrOf.get(cdt0) ?? "brand"),
                    carrier: "cdt2",
                    part: brandPrefix,
                  },
                ]
              : [],
        };
        let node: TreeNode | null = tree;
        let phase: "top" | "cdt2" | "cdt3" = "top";
        while (node) {
          const value = attrValueOf(item, node.attr);
          const kid = node.kids.find((entry) => entry.value === value);
          if (!kid) {
            // Stop rather than invent a branch when source and inferred values disagree.

            if (!pathBreakReported) {
              pathBreakReported = true;
              fail(
                "path-broken",
                `"${cdt0} › ${cdt1}": ${item.name.slice(0, 40)} reads "${value}" for ${node.attr}, which is none of the split's values [${node.kids.map((entry) => entry.value).join(", ")}] — the label stops at this split.`,
              );
            }
            break;
          }
          const partition = node.reading === "vertical" && rowPartition(node);
          const step = (carrier: ReplicaPathStep["carrier"], part: string | null): void => {
            path.steps.push({ attr: node!.attr, value, carrier, part });
          };
          if (partition && phase === "top") {
            if (!reported.has(node)) {
              reported.add(node);
              let recorded = 0;
              for (const entry of node.kids) {
                const entryItems = kidItemsOf(node, entry.value);
                const rule: ReplicaRowHome = {
                  cdt0,
                  cdt1,
                  attr: node.attr,
                  value: entry.value,
                  shelves: shelvesOf(entryItems),
                  skuIds: entryItems.map((entryItem) => entryItem.skuId),
                  shipped: false,
                };
                if (privateRowHomeKeys.has(rowHomeKeyOf(rule))) continue;
                privateRowHomeKeys.add(rowHomeKeyOf(rule));
                privateRowHomes.push(rule);
                recorded += 1;
              }
              advisories.push(
                `"${cdt0} › ${cdt1}" opens on a brand-private row partition, ${describeNode(node)} — no other brand of "${cdt0}" follows it, so it is not carved into a cdt0 (§9: upward only when shared); folded into the cdt3 label, lossy${recorded > 0 ? "" : " (recorded by the 2b pre-pass)"}.`,
              );
            }
            path.cdt3Parts.push(value);
            path.cdt3Nodes.push(node);
            step("rowHome", value);
          } else if (partition) {
            if (!reported.has(node)) {
              reported.add(node);
              advisories.push(
                `"${cdt0} › ${cdt1}" has a vertical split UNDER a horizontal one (${describeNode(node)}); pogger can only approximate it by fill order — folded into the cdt3 label, lossy.`,
              );
            }
            phase = "cdt3";
            path.cdt3Parts.push(value);
            path.cdt3Nodes.push(node);
            step("lossy", value);
          } else {
            if (node.reading === "vertical" && !reported.has(node)) {
              reported.add(node);
              advisories.push(
                `"${cdt0} › ${cdt1}" reads ${describeNode(node)} as vertical, but only one value is a multi-item block (the rest are seam singles), so it owns every row of the node — not a row partition; read as a horizontal split.`,
              );
            }
            const takeAsCdt2 =
              cdt2Mode === "windows"
                ? phase !== "cdt3" && windowSplit(node) && !isLadder(node)
                : phase === "top";
            if (takeAsCdt2) {
              phase = "cdt2";
              if (isLadder(node) && !singleRow) {
                const group = ladderGroupPart(node, value);
                path.cdt2Parts.push(group);
                path.cdt2Attrs.push(node.attr);
                step("cdt2", group);

                const ladderNode: TreeNode = node;
                const groupKids = ladderNode.kids.filter(
                  (kid) => ladderGroupPart(ladderNode, kid.value) === group,
                );
                const rung = partsOverKids(groupKids).get(value) ?? null;
                if (rung !== null) path.cdt3Parts.push(rung);
                path.cdt3Nodes.push(node);
                step(rung === null ? "seq" : "cdt3", rung);
                phase = "cdt3";
              } else {
                const part = value === NONE_VALUE ? `no ${node.attr}` : value;
                path.cdt2Parts.push(part);
                path.cdt2Attrs.push(node.attr);
                step("cdt2", part);
              }
            } else {
              phase = "cdt3";
              const part = cdt3PartsOf(node).get(value) ?? null;
              if (part !== null) path.cdt3Parts.push(part);
              path.cdt3Nodes.push(node);
              step(part === null ? "seq" : "cdt3", part);
            }
          }
          node = kid.node;
        }
        return path;
      };

      const treeRank = new Map<string, number>();
      const visit = (node: TreeNode | null, nodeItems: readonly StudioItem[]): void => {
        if (!node) {
          for (const item of [...nodeItems].sort((a, b) => rankIn([a.skuId]) - rankIn([b.skuId]))) {
            if (!treeRank.has(item.skuId)) treeRank.set(item.skuId, treeRank.size);
          }
          return;
        }
        for (const kid of node.kids) visit(kid.node, kidItemsOf(node, kid.value));
      };
      visit(tree, columnItems);
      visit(null, columnItems);
      const treeRankIn = (skuItems: readonly StudioItem[]): number =>
        Math.min(...skuItems.map((item) => treeRank.get(item.skuId) ?? Number.MAX_SAFE_INTEGER));

      const paths = new Map(columnItems.map((item) => [item.skuId, pathOf(item)]));
      if (
        tree &&
        columnItems.length > 1 &&
        [...paths.values()].every(
          (path) => path.cdt2Parts.length === (brandPrefix !== null ? 1 : 0),
        )
      ) {
        advisories.push(
          `"${cdt0} › ${cdt1}${brandPrefix !== null ? ` › ${brandPrefix}` : ""}": cdt2 repeats cdt1 — no horizontal split in the node (its only split is a row partition, folded into cdt3).`,
        );
      }
      const byBlock = new Map<string, StudioItem[]>();
      for (const item of columnItems) {
        const blockValue = composeLabel(paths.get(item.skuId)!.cdt2Parts, cdt1);
        (byBlock.get(blockValue) ?? byBlock.set(blockValue, []).get(blockValue)!).push(item);
      }
      const blocks = [...byBlock.entries()].sort((a, b) => treeRankIn(a[1]) - treeRankIn(b[1]));

      blocks.forEach(([blockValue, blockItems], blockIdx) => {
        const labelOf = (item: StudioItem): string =>
          composeLabel(paths.get(item.skuId)!.cdt3Parts, blockValue);
        const labelRank = new Map<string, number>();
        for (const item of blockItems) {
          const label = labelOf(item);
          const rank = treeRankIn([item]);
          if (!labelRank.has(label) || rank < labelRank.get(label)!) labelRank.set(label, rank);
        }
        const orderedLabels = [...labelRank.keys()].sort(
          (a, b) => labelRank.get(a)! - labelRank.get(b)!,
        );
        const rankOfLabel = new Map(orderedLabels.map((label, index) => [label, index + 1]));

        const chainNodes: TreeNode[] = [];
        for (const item of blockItems) {
          for (const node of paths.get(item.skuId)!.cdt3Nodes)
            if (!chainNodes.includes(node)) chainNodes.push(node);
        }
        const leadItem = [...blockItems].sort((a, b) => rankIn([a.skuId]) - rankIn([b.skuId]))[0];
        const leadPath = paths.get(leadItem.skuId)!;
        const ruleDescription =
          chainNodes.length > 0
            ? chainNodes.map(describeNode).join(" then ")
            : "wall order (no further attribute varies)";

        if (blockItems.length > 1 && orderedLabels.length <= 1) {
          const spent = new Set([
            ...leadPath.cdt2Attrs,
            ...leadPath.cdt3Nodes.map((node) => node.attr),
          ]);
          const stillVarying = nodeCandidates.filter((candidate) => {
            if (spent.has(candidate)) return false;
            const values = new Set(blockItems.map((item) => attrValueOf(item, candidate)));
            return values.size > 1;
          });
          if (stillVarying.length > 0) {
            advisories.push(
              `"${cdt0} › ${cdt1} › ${blockValue}" carries ${blockItems.length} SKUs with cdt3 constant — [${stillVarying.join(", ")}] still vary(s) but none cleared the 75% purity floor (or only as singleton leaves, which ride walk_rank); needs a ruling only if the wall disagrees.`,
            );
          } else {
            warn(
              "constant-tiers",
              `"${cdt0} › ${cdt1} › ${blockValue}" carries ${blockItems.length} SKUs with cdt2 AND cdt3 both constant — nothing in the candidate attributes distinguishes them.`,
            );
          }
        }

        const firstCdt3 = leadPath.cdt3Nodes[0];
        ruleReports.push({
          cdt0,
          cdt1,
          cdt2: blockValue,
          cdt2Attr: leadPath.cdt2Attrs.length > 0 ? leadPath.cdt2Attrs.join(" — ") : null,
          block_order: blockIdx + 1,
          rule: ruleDescription,
          attr: firstCdt3?.attr ?? null,
          direction: (firstCdt3?.direction.trim() || "ascending") as "ascending" | "descending",
          labels: orderedLabels,
          pinnedLast: [],
        });

        const pair = pairs.pairs.find((entry) => entry.cdt0 === cdt0 && entry.cdt1 === cdt1);
        for (const item of blockItems) {
          const label = labelOf(item);
          treeItems[item.skuId] = {
            cdt0,
            cdt1,
            cdt2: blockValue,
            cdt3: label,
            cdt1_priority: pairs.priorityOf.get(pairKeyOf(cdt0, cdt1)) ?? 0,
            cdt2_priority: blockIdx + 1,
            cdt3_priority: rankOfLabel.get(label) ?? Math.max(orderedLabels.length, 1),
            segment: pair?.segment ?? cdt1,
            walk_rank: walk.rankOf.get(item.skuId) ?? Number.MAX_SAFE_INTEGER,
            path: paths.get(item.skuId)!.steps,
          };
        }
      });
    }
  }

  {
    const nodeSize = new Map<string, number>();
    for (const entry of Object.values(treeItems)) {
      const key = pairKeyOf(entry.cdt0, entry.cdt1);
      nodeSize.set(key, (nodeSize.get(key) ?? 0) + 1);
    }
    const blockSize = new Map<string, number>();
    for (const entry of Object.values(treeItems)) {
      const key = [entry.cdt0, entry.cdt1, entry.cdt2].join(PATH_SEPARATOR);
      blockSize.set(key, (blockSize.get(key) ?? 0) + 1);
    }
    let cdt2RepeatsOneItem = 0;
    let cdt2RepeatsMulti = 0;
    let cdt3RepeatsOneItem = 0;
    let cdt3RepeatsMulti = 0;
    for (const entry of Object.values(treeItems)) {
      if (entry.cdt2 === entry.cdt1) {
        if ((nodeSize.get(pairKeyOf(entry.cdt0, entry.cdt1)) ?? 0) <= 1) cdt2RepeatsOneItem += 1;
        else cdt2RepeatsMulti += 1;
      }
      if (entry.cdt3 === entry.cdt2) {
        if ((blockSize.get([entry.cdt0, entry.cdt1, entry.cdt2].join(PATH_SEPARATOR)) ?? 0) <= 1)
          cdt3RepeatsOneItem += 1;
        else cdt3RepeatsMulti += 1;
      }
    }
    advisories.push(
      `Level repeats: cdt2 == cdt1 on ${cdt2RepeatsOneItem} item(s) in one-item nodes and ${cdt2RepeatsMulti} in multi-item nodes (the latter only where the node has no horizontal split); cdt3 == cdt2 on ${cdt3RepeatsOneItem} item(s) in one-item blocks and ${cdt3RepeatsMulti} in multi-item blocks with no split remaining.`,
    );
  }

  for (const pool of Object.values(pools)) {
    const entries = Object.values(treeItems).filter(
      (entry) => entry.cdt0 === pool.cdt0 && entry.cdt1 === pool.cdt1,
    );
    const firstRankOfBlock = new Map<string, number>();
    for (const entry of entries) {
      if (entry.walk_rank < (firstRankOfBlock.get(entry.cdt2) ?? Number.MAX_SAFE_INTEGER)) {
        firstRankOfBlock.set(entry.cdt2, entry.walk_rank);
      }
    }
    const orderOfBlock = new Map(
      [...firstRankOfBlock.entries()]
        .sort((a, b) => a[1] - b[1])
        .map(([cdt2], index) => [cdt2, index + 1]),
    );
    for (const entry of entries)
      entry.cdt2_priority = orderOfBlock.get(entry.cdt2) ?? entry.cdt2_priority;
    for (const report of ruleReports) {
      if (report.cdt0 === pool.cdt0 && report.cdt1 === pool.cdt1) {
        report.block_order = orderOfBlock.get(report.cdt2) ?? report.block_order;
      }
    }
  }

  const labelPriorities = new Map<string, Map<string, number>>();
  for (const entry of Object.values(treeItems)) {
    const node = [entry.cdt0, entry.cdt1, entry.cdt2].join(PATH_SEPARATOR);
    const seen = labelPriorities.get(node) ?? labelPriorities.set(node, new Map()).get(node)!;
    const existing = seen.get(entry.cdt3);
    if (existing !== undefined && existing !== entry.cdt3_priority) {
      fail(
        "cdt3-label-priority",
        `"${node}" gives label "${entry.cdt3}" both priority ${existing} and ${entry.cdt3_priority}; pogger requires one priority per LABEL.`,
      );
    }
    seen.set(entry.cdt3, entry.cdt3_priority);
  }
  const priorityNumbers = [...new Set(pairs.pairs.map((pair) => pair.priority))].sort(
    (a, b) => a - b,
  );
  if (!priorityNumbers.every((number, index) => number === index + 1)) {
    fail(
      "cdt1-priority-gap",
      `pair priorities run ${priorityNumbers[0]}..${priorityNumbers[priorityNumbers.length - 1]} over ${priorityNumbers.length} pairs, with gaps.`,
    );
  }

  const homed = withRowHomeStrata(strataResult, rowHomes);
  for (const warning of homed.rowHomeWarnings) advisories.push(warning);
  const shippedCdt0s = new Set(Object.values(treeItems).map((entry) => entry.cdt0));
  const shippedRowHomeStrata = homed.rowHomeStrata.filter((stratum) =>
    shippedCdt0s.has(stratum.cdt0),
  );
  for (const stratum of homed.rowHomeStrata) {
    if (shippedCdt0s.has(stratum.cdt0)) continue;
    warn(
      "row-home-unshipped",
      `"${stratum.cdt0}" (rows ${stratum.homeShelves.join(",")}, ${stratum.skuIds.length} SKUs of "${stratum.section}") is a vertical split the tree carries under cdt1, not as a cdt0: its row-home is reported on restrictions.rowHomeStrata and NOT emitted as rows — adopt the sub-stratum (withRowHomeStrata) before priorities are numbered to bind it.`,
    );
  }

  // Restrictions may name only CDT0 values present in the shipped tree.
  const emittedStrata = [...strataResult.strata, ...shippedRowHomeStrata].filter(
    (stratum) => stratum.skuIds.length > 0,
  );
  const { rows, anchors } = materializeRestrictionRows(emittedStrata, partition, runs, items);
  const rowStrata: Record<string, readonly number[]> = {};
  for (const stratum of emittedStrata) rowStrata[stratum.cdt0] = stratum.homeShelves;

  {
    const run0 = runs[0];
    const widthOfSku = new Map(
      items.map((item) => [
        item.skuId,
        item.placements.reduce((sum, placement) => sum + placement.widthCm, 0),
      ]),
    );
    const totalWidth = [...widthOfSku.values()].reduce((sum, width) => sum + width, 0);
    const shelfCount = new Set((run0?.shelves ?? []).map((shelf) => shelf.rank)).size;
    if (run0 && run0.bayWidthCm > 0 && shelfCount > 0 && totalWidth > 0) {
      for (const zone of partition.zones) {
        const zoneWidth = items
          .filter((item) => zone.sections.includes(String(item.attrs[sectionAttr] ?? "")))
          .reduce((sum, item) => sum + (widthOfSku.get(item.skuId) ?? 0), 0);
        const capacity = zone.bays.length * run0.bayWidthCm * shelfCount;
        advisories.push(
          `space: zone "${zone.sections.join(" + ")}" sales share ${(100 * zone.share).toFixed(1)}% buys ${zone.bays.length} bay(s) = ${capacity.toFixed(0)} cm of shelf for ${zoneWidth.toFixed(0)} cm of product on the wall (fill ${((100 * zoneWidth) / capacity).toFixed(0)}%; width share ${((100 * zoneWidth) / totalWidth).toFixed(1)}%).`,
        );
      }
      for (const stratum of emittedStrata) {
        const bays = partition.zones[stratum.zone]?.bays ?? [];
        const supply = bays.length * run0.bayWidthCm * stratum.homeShelves.length;
        const demand = stratum.skuIds.reduce((sum, skuId) => sum + (widthOfSku.get(skuId) ?? 0), 0);
        if (!(supply > 0)) continue;
        advisories.push(
          `space: "${stratum.cdt0}" holds ${demand.toFixed(0)} cm of product for ${supply.toFixed(0)} cm of shelf (${bays.length} bay(s) × rows ${stratum.homeShelves.join(",")}) — fill ${((100 * demand) / supply).toFixed(0)}%${stratum.fullHeight ? " (no row fence; shares its rows)" : ""}.`,
        );
      }
    }
  }

  // Keep the legacy key although each anchor belongs to its zone's first bay.
  const bay1Anchors: Record<string, readonly number[]> = {};
  for (const anchor of anchors) {
    bay1Anchors[anchor.variant] = [...(bay1Anchors[anchor.variant] ?? []), Number(anchor.shelf)];
  }

  if (partition.zones.length > 1) {
    let spilled = 0;
    for (const stratum of strataResult.strata) {
      const bays = new Set(partition.zones[stratum.zone]?.bays ?? []);
      for (const skuId of stratum.skuIds) {
        const first = walk.firstPlacement.get(skuId);
        if (first && !bays.has(first.bay)) spilled += 1;
      }
    }
    if (spilled > 0) {
      advisories.push(
        `${spilled} SKUs sit outside their partition zone on the base pog itself; the partition re-homes them.`,
      );
    }
  }

  const pins: Record<
    string,
    { min_facings: number; max_facings: number; min_days_of_supply: number }
  > = {};
  for (const item of items) {
    pins[item.skuId] = {
      min_facings: item.facings,
      max_facings: item.facings,
      min_days_of_supply: 0,
    };
  }

  // Pogger uses candidate order as the final tie-break within a label.
  const orderedTreeItems = Object.fromEntries(
    Object.entries(treeItems).sort((a, b) => a[1].walk_rank - b[1].walk_rank),
  );

  const zoneSeams: { leftRun: number; rightRun: number }[] = [];
  for (let index = 0; index + 1 < partition.zones.length; index += 1) {
    const own = partition.zones[index].bays;
    const next = partition.zones[index + 1].bays;
    const leftRun = own[own.length - 1];
    const rightRun = next[0];
    if (leftRun !== undefined && rightRun !== undefined) zoneSeams.push({ leftRun, rightRun });
  }

  const rules: RuleSet = (() => {
    // Child stores re-evaluate pooling, so persist pair order by real brand.
    const rawPairs = pairPriorities(items, walk, cdt0Of, rawCdt1Of, priorityCdt0Order, sectionOf);

    // Preserve unmerged zones so each child fixture can decide its own seam.
    const zoneCandidates =
      shippedZones ??
      deriveZoneShareRules(items, sectionAttr, runs.length, runs[0], { merge: false });
    const attrsSeenInTrees = new Set<string>();
    const walkTree = (node: RuleTreeNode | null): void => {
      if (!node) return;
      attrsSeenInTrees.add(node.attr);
      for (const kid of node.kids) walkTree(kid.node);
    };
    for (const node of ruleNodes) walkTree(node.tree);
    const sectionOfCdt0: Record<string, string> = {};
    const cdt1AttrOfCdt0: Record<string, string> = {};
    for (const item of items) {
      const cdt0 = cdt0Of(item);
      if (!(cdt0 in sectionOfCdt0)) sectionOfCdt0[cdt0] = sectionOf(item);
      if (!(cdt0 in cdt1AttrOfCdt0)) cdt1AttrOfCdt0[cdt0] = cdt1AttrOf.get(cdt0) ?? "";
    }
    const skus: RuleSkuFacts[] = items.map((item) => ({
      skuId: item.skuId,
      cdt0: cdt0Of(item),
      cdt1: rawCdt1Of(item),
      section: sectionOf(item),
      walkRank: walk.rankOf.get(item.skuId) ?? Number.MAX_SAFE_INTEGER,
      baseFacings: item.facings,
      attrs: Object.fromEntries(
        [...attrsSeenInTrees].map((attr) => [attr, attrValueOf(item, attr)]),
      ),
    }));
    const basePools: RulePool[] = Object.entries(pools).map(([key, pool]) => ({
      key,
      cdt0: pool.cdt0,
      cdt1: pool.cdt1,
      ...(pool.host ? { host: pool.host } : {}),
      members: pool.members.map((member) => member.cdt1),
    }));

    const carves: RuleCarve[] = [];
    {
      const sidesOfParent = new Map<string, { side: string; top: number }[]>();
      for (const stratum of preHomed.rowHomeStrata) {
        const rule = preRowHomes.find((entry) => entry.skuIds.includes(stratum.skuIds[0] ?? ""));
        const parentCdt0 = parentOfSubStratum.get(stratum.cdt0);
        if (!rule || !parentCdt0) continue;
        const list =
          sidesOfParent.get(parentCdt0) ?? sidesOfParent.set(parentCdt0, []).get(parentCdt0)!;
        if (!list.some((entry) => entry.side === rule.value)) {
          list.push({ side: rule.value, top: Math.min(...rule.shelves) });
        }
        carves.push({
          cdt0: stratum.cdt0,
          parentCdt0,
          attr: rule.attr,
          side: rule.value,
          sideOrder: 0,
        });
      }
      for (const carve of carves) {
        const ordered = (sidesOfParent.get(carve.parentCdt0) ?? [])
          .slice()
          .sort((a, b) => a.top - b.top)
          .map((entry) => entry.side);
        carve.sideOrder = Math.max(0, ordered.indexOf(carve.side));
      }
    }
    for (const carve of carves) {
      if (!(carve.parentCdt0 in sectionOfCdt0)) {
        sectionOfCdt0[carve.parentCdt0] = sectionOfCdt0[carve.cdt0] ?? carve.parentCdt0;
      }
      if (!(carve.parentCdt0 in cdt1AttrOfCdt0)) {
        cdt1AttrOfCdt0[carve.parentCdt0] = cdt1AttrOfCdt0[carve.cdt0] ?? "";
      }
    }
    return {
      cdt0Order: pairs.cdt0Order,

      // A folded carve can revive its parent even when the base ships only its children.
      frozenCdt0Names: [
        ...new Set([
          ...pairs.cdt0Order,
          ...shippedCdt0s,
          ...carves.map((carve) => carve.parentCdt0),
        ]),
      ],
      cdt1AttrOfCdt0,
      sectionOfCdt0,
      pairOrder: rawPairs.pairs.map((pair) => ({ cdt0: pair.cdt0, cdt1: pair.cdt1 })),
      nodes: ruleNodes,
      skus,
      basePools,
      pooling: {
        subFloorShare: SUB_FLOOR_SHARE,

        // Mirrors Pogger's minimum viable width of 2.5 widest-pack facings.
        subFloorFacings: 2.5,
        oneSku: true,
        topExempt: TOP_SALES_EXEMPT,
        cap: POOL_CAP,
      },
      carves,
      baseShelfCm:
        (runs[0]?.bays ?? 0) *
        (runs[0]?.bayWidthCm ?? 0) *
        new Set((runs[0]?.shelves ?? []).map((shelf) => shelf.rank)).size,
      partition: zoneCandidates,
      strata: emittedStrata,
      baseDemandCm: Object.fromEntries(
        emittedStrata.map((stratum) => [
          stratum.cdt0,
          stratum.skuIds.reduce((sum, skuId) => {
            const item = items.find((entry) => entry.skuId === skuId);
            return sum + (item?.placements.reduce((own, p) => own + p.widthCm, 0) ?? 0);
          }, 0),
        ]),
      ),
      minDemandCm: Object.fromEntries(
        emittedStrata.map((stratum) => [
          stratum.cdt0,
          stratum.skuIds.reduce((sum, skuId) => {
            const item = items.find((entry) => entry.skuId === skuId);
            return sum + (item?.facingWidthCm ?? 0);
          }, 0),
        ]),
      ),
      rowHomes: [...preRowHomes, ...privateRowHomes, ...rowHomes],
    };
  })();

  return {
    tree: { items: orderedTreeItems, cdt0Order: pairs.cdt0Order, segments: pairs.pairs },
    restrictions: {
      rules: {
        partition: partitionRules,
        anchorRule: "the first bay of each zone anchors its own strata",
      },
      rows,
      anchors,
      partition,
      rowStrata,
      bay1Anchors,
      zoneSeams,
      rowHomeStrata: homed.rowHomeStrata,
    },
    pins,
    orderingRules: ruleReports,
    rowHomes: [...preRowHomes, ...privateRowHomes, ...rowHomes],
    pools,
    poolViability,
    rules,
    advisories,
    findings,
  };
};
